import type { QuestionDropdownModel } from "survey-core";
import { Select } from "@rumbledirect/ui-v2";
import { cn } from "@rumbledirect/ui-v2/cn";
import { useSurveyQuestionRerender } from "./useSurveyQuestionRerender";

/**
 * Renderer for SurveyJS `dropdown` questions using admin-v2's (native)
 * `Select`. Static `choices` and `choicesByUrl` are both supported: remote
 * choices resolve through survey-core's `ChoicesRestful`, rerouted onto
 * admin-v2's authenticated API client and narrowed by the question's
 * optional `companyType` (see `registerChoicesByUrl`). The placeholder is a
 * disabled empty-valued option so the native select can show it without
 * being selectable; choosing it is impossible, so "no selection" is always
 * represented by the model value being empty. `wrapperClassName` stretches
 * the inline-flex wrapper so the select fills the field like `Input` does.
 */
export function SurveyDropdownQuestion({
  question,
}: {
  readonly question: QuestionDropdownModel;
}) {
  useSurveyQuestionRerender(question);
  const value = question.value;
  const renderedPlaceholder = question.renderedPlaceholder as
    | string
    | undefined;
  const stringValue =
    value === undefined || value === null ? "" : String(value);

  return (
    <Select
      id={question.inputId}
      wrapperClassName="flex w-full"
      className={cn(stringValue === "" && "text-muted-foreground")}
      value={stringValue}
      // Native `<select>` has no `readonly` behaviour (the HTML attribute is
      // silently ignored), so a read-only survey must disable the control
      // instead — the same fallback the shared package's checkbox/file
      // renderers use for the same reason.
      disabled={question.isDisabledAttr || question.isReadOnlyAttr}
      onChange={(event) => {
        question.value =
          event.target.value === "" ? undefined : event.target.value;
      }}
      onBlur={(event) => question.onBlur(event)}
      onFocus={(event) => question.onFocus(event)}
      aria-required={question.a11y_input_ariaRequired}
      aria-invalid={question.a11y_input_ariaInvalid}
      aria-describedby={question.a11y_input_ariaDescribedBy ?? undefined}
    >
      <option value="" disabled={question.isRequired}>
        {renderedPlaceholder ?? ""}
      </option>
      {question.visibleChoices.map((choice) => (
        <option key={String(choice.value)} value={String(choice.value)}>
          {choice.text}
        </option>
      ))}
    </Select>
  );
}
